import { useState, useEffect } from "react";

const DateRangePicker = ({ onClose, onDateSelect }) => {
  // Helper functions for date handling
  const getFormattedDate = (date) => {
    return date.toLocaleDateString("en-GB"); // Formats date as "DD/MM/YYYY"
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    // 0 = Sunday, 1 = Monday, etc.
    const day = new Date(year, month, 1).getDay();
    // Convert to our calendar format (0 = Monday, 6 = Sunday)
    return day === 0 ? 6 : day - 1;
  };

  // Get current date
  const today = new Date();
  
  // State for selected dates
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(() => {
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    return nextWeek;
  });
  
  // State for calendar display
  const [leftMonth, setLeftMonth] = useState({
    month: today.getMonth(),
    year: today.getFullYear()
  });
  
  const [rightMonth, setRightMonth] = useState(() => {
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    return {
      month: nextMonth.getMonth(),
      year: nextMonth.getFullYear()
    };
  });
  
  // State for maintaining selection mode
  const [selectionMode, setSelectionMode] = useState('start'); // 'start' or 'end'

  // Handle date selection
  const handleDateClick = (date) => {
    if (selectionMode === 'start' || date < startDate) {
      setStartDate(date);
      setEndDate(null);
      setSelectionMode('end');
    } else {
      setEndDate(date);
      setSelectionMode('start'); // Reset for next selection
    }
  };

  // Handle preset selections
  const handlePresetSelect = (preset) => {
    const now = new Date();
    
    switch(preset) {
      case 'today':
        setStartDate(now);
        setEndDate(now);
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case 'thisWeek':
        const thisWeekStart = new Date(now);
        thisWeekStart.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1)); // Monday of current week
        setStartDate(thisWeekStart);
        setEndDate(now);
        break;
      case 'lastWeek':
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 6);
        const lastWeekEnd = new Date(now);
        lastWeekEnd.setDate(now.getDate() - now.getDay());
        setStartDate(lastWeekStart);
        setEndDate(lastWeekEnd);
        break;
      case 'thisMonth':
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(thisMonthStart);
        setEndDate(now);
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        setStartDate(lastMonthStart);
        setEndDate(lastMonthEnd);
        break;
      case 'thisYear':
        const thisYearStart = new Date(now.getFullYear(), 0, 1);
        setStartDate(thisYearStart);
        setEndDate(now);
        break;
      case 'lastYear':
        const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);
        setStartDate(lastYearStart);
        setEndDate(lastYearEnd);
        break;
      case 'allTime':
        // For "all time", you might want to use a very early start date
        const allTimeStart = new Date(2000, 0, 1);
        setStartDate(allTimeStart);
        setEndDate(now);
        break;
      default:
        break;
    }
    
    // After preset selection, reset to 'start' mode for manual selection
    setSelectionMode('start');
  };

  // Navigate months
  const navigateMonth = (calendar, direction) => {
    if (calendar === 'left') {
      const newMonth = { ...leftMonth };
      if (direction === 'prev') {
        if (newMonth.month === 0) {
          newMonth.month = 11;
          newMonth.year -= 1;
        } else {
          newMonth.month -= 1;
        }
      } else {
        if (newMonth.month === 11) {
          newMonth.month = 0;
          newMonth.year += 1;
        } else {
          newMonth.month += 1;
        }
      }
      setLeftMonth(newMonth);
    } else {
      const newMonth = { ...rightMonth };
      if (direction === 'prev') {
        if (newMonth.month === 0) {
          newMonth.month = 11;
          newMonth.year -= 1;
        } else {
          newMonth.month -= 1;
        }
      } else {
        if (newMonth.month === 11) {
          newMonth.month = 0;
          newMonth.year += 1;
        } else {
          newMonth.month += 1;
        }
      }
      setRightMonth(newMonth);
    }
  };

  // Keep right month synchronized when left month changes
  useEffect(() => {
    if (leftMonth.month === 11) {
      setRightMonth({
        month: 0,
        year: leftMonth.year + 1
      });
    } else {
      setRightMonth({
        month: leftMonth.month + 1,
        year: leftMonth.year
      });
    }
  }, [leftMonth]);

  // Handle Apply button
  const handleApply = () => {
    onDateSelect({
      startDate: getFormattedDate(startDate),
      endDate: getFormattedDate(endDate || startDate)
    });
    onClose();
  };

  // Function to check if a date is selected (for highlighting)
  const isDateSelected = (date) => {
    const dateTime = date.getTime();
    const startDateTime = startDate ? startDate.getTime() : 0;
    const endDateTime = endDate ? endDate.getTime() : 0;
    
    if (startDateTime === dateTime) return 'start';
    if (endDateTime === dateTime) return 'end';
    if (startDateTime && endDateTime && dateTime > startDateTime && dateTime < endDateTime) return 'range';
    return '';
  };

  // Check if date is in current month's display
  const isDateInCalendarDisplay = (date, calendarMonth, calendarYear) => {
    const dateMonth = date.getMonth();
    const dateYear = date.getFullYear();
    
    // Same month and year (current month days)
    if (dateMonth === calendarMonth && dateYear === calendarYear) {
      return true;
    }
    
    // Prev month filler days
    const firstDay = getFirstDayOfMonth(calendarYear, calendarMonth);
    if (firstDay > 0 && 
        ((dateMonth === calendarMonth-1 || (calendarMonth === 0 && dateMonth === 11 && dateYear === calendarYear-1)))) {
      const daysInPrevMonth = getDaysInMonth(
        calendarMonth === 0 ? calendarYear - 1 : calendarYear, 
        calendarMonth === 0 ? 11 : calendarMonth - 1
      );
      const day = date.getDate();
      return day > daysInPrevMonth - firstDay;
    }
    
    // Next month filler days
    const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
    const totalCells = 6 * 7; // 6 rows of 7 days
    const filledCells = firstDay + daysInMonth;
    const nextMonthDays = totalCells - filledCells;
    
    if (nextMonthDays > 0 && 
        ((dateMonth === calendarMonth+1 || (calendarMonth === 11 && dateMonth === 0 && dateYear === calendarYear+1)))) {
      return date.getDate() <= nextMonthDays;
    }
    
    return false;
  };

  // Function to render a month calendar
  const renderCalendar = (year, month) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    // Get days from previous month to fill first week
    const daysFromPrevMonth = firstDay;
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevMonthYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);
    
    // Always show 6 weeks for consistent height
    const totalDaysDisplayed = 6 * 7;
    const daysFromNextMonth = totalDaysDisplayed - daysFromPrevMonth - daysInMonth;
    
    const weeks = [];
    let days = [];
    
    // Previous month days
    for (let i = 0; i < daysFromPrevMonth; i++) {
      const day = daysInPrevMonth - daysFromPrevMonth + i + 1;
      const date = new Date(prevMonthYear, prevMonth, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        selectionState: isDateSelected(date)
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        day: i,
        isCurrentMonth: true,
        selectionState: isDateSelected(date)
      });
      
      if (days.length === 7) {
        weeks.push([...days]);
        days = [];
      }
    }
    
    // Next month days
    for (let i = 1; i <= daysFromNextMonth; i++) {
      const date = new Date(year, month + 1 === 12 ? 0 : month + 1, i);
      const nextMonthYear = month === 11 ? year + 1 : year;
      days.push({
        date: new Date(nextMonthYear, month === 11 ? 0 : month + 1, i),
        day: i,
        isCurrentMonth: false,
        selectionState: isDateSelected(date)
      });
      
      if (days.length === 7) {
        weeks.push([...days]);
        days = [];
      }
    }
    
    if (days.length > 0) {
      weeks.push([...days]);
    }
    
    return weeks;
  };

  // Generate month names
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Generate day names
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Generate calendar data
  const leftCalendar = renderCalendar(leftMonth.year, leftMonth.month);
  const rightCalendar = renderCalendar(rightMonth.year, rightMonth.month);

  return (
    <div className="relative shadow-[0px_10px_60px_rgba(0,_0,_0,_0.1)] rounded-3xs box-border flex flex-row items-start justify-start max-w-full max-h-full overflow-auto text-left text-sm text-neutrals-neutral-900 font-open-sans-ff-meta-link border-[1px] border-solid border-neutrals-neutral-100">
      {/* Left sidebar with presets */}
      <div className="self-stretch rounded-tl-3xs rounded-tr-none rounded-br-none rounded-bl-3xs bg-neutrals-neutral-50 box-border flex flex-col items-start justify-start py-3 px-4 gap-[6px] border-r-[1px] border-solid border-neutrals-neutral-100">
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('today')}
        >
          <div className="relative font-semibold">Today</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('yesterday')}
        >
          <div className="relative font-semibold">Yesterday</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('thisWeek')}
        >
          <div className="relative font-semibold">This week</div>
        </div>
        <div 
          className="w-40 rounded-3xs bg-primary-primary-50 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer"
          onClick={() => handlePresetSelect('lastWeek')}
        >
          <div className="relative font-semibold">Last week</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('thisMonth')}
        >
          <div className="relative font-semibold">This month</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('lastMonth')}
        >
          <div className="relative font-semibold">Last month</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('thisYear')}
        >
          <div className="relative font-semibold">This year</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('lastYear')}
        >
          <div className="relative font-semibold">Last year</div>
        </div>
        <div 
          className="w-40 flex flex-row items-center justify-start py-2 px-4 box-border cursor-pointer hover:bg-primary-primary-50 rounded-3xs"
          onClick={() => handlePresetSelect('allTime')}
        >
          <div className="relative font-semibold">All time</div>
        </div>
      </div>

      {/* Calendar section */}
      <div className="flex flex-col items-start justify-start text-base">
        <div className="flex flex-row items-start justify-start border-b-[1px] border-solid border-neutrals-neutral-100">
          {/* Left calendar */}
          <div className="bg-neutrals-neutral-50 flex flex-col items-start justify-start py-5 px-6 gap-[10px] border-r-[1px] border-solid border-neutrals-neutral-100">
            <div className="w-[280px] flex flex-row items-center justify-between">
              <div 
                className="flex flex-row items-start justify-start p-2.5 cursor-pointer"
                onClick={() => navigateMonth('left', 'prev')}
              >
                <div className="w-5 relative h-5 flex items-center justify-center">←</div>
              </div>
              <div className="relative font-semibold">{monthNames[leftMonth.month]} {leftMonth.year}</div>
              <div 
                className="flex flex-row items-start justify-start p-2.5 cursor-pointer"
                onClick={() => navigateMonth('left', 'next')}
              >
                <div className="w-5 relative h-5 flex items-center justify-center">→</div>
              </div>
            </div>
            <div className="flex flex-col items-start justify-start gap-[4px] text-sm">
              {/* Day headers */}
              <div className="flex flex-row items-start justify-start">
                {dayNames.map((day, index) => (
                  <div key={index} className="w-10 h-10 flex flex-row items-center justify-center p-2.5 box-border">
                    <div className="relative font-semibold">{day}</div>
                  </div>
                ))}
              </div>
              {/* Calendar days */}
              <div className="flex flex-col items-start justify-start gap-[4px]">
                {leftCalendar.map((week, weekIndex) => (
                  <div key={`left-week-${weekIndex}`} className="flex flex-row items-start justify-start">
                    {week.map((day, dayIndex) => {
                      const isStart = day.selectionState === 'start';
                      const isEnd = day.selectionState === 'end';
                      const isRange = day.selectionState === 'range';
                      
                      // Check if this date is shown in the right calendar
                      // and is from an adjacent month (not current month)
                      const isOverlap = !day.isCurrentMonth && 
                                     isDateInCalendarDisplay(day.date, rightMonth.month, rightMonth.year);
                      
                      if (isOverlap) {
                        // Return an empty cell for overlapping dates
                        return (
                          <div 
                            key={`left-day-${weekIndex}-${dayIndex}`} 
                            className="w-10 h-10 flex flex-row items-center justify-center p-2.5 box-border"
                          >
                            <div className="relative text-neutrals-neutral-50">.</div>
                          </div>
                        );
                      }
                      
                      let className = "w-10 h-10 flex flex-row items-center justify-center p-2.5 box-border cursor-pointer";
                      if (!day.isCurrentMonth) className += " text-neutrals-neutral-300";
                      if (isStart) className += " rounded-tl-3xs rounded-bl-3xs bg-primary-primary-500 text-neutrals-neutral-50";
                      if (isEnd) className += " rounded-tr-3xs rounded-br-3xs bg-primary-primary-500 text-neutrals-neutral-50";
                      if (isRange) className += " bg-primary-primary-50 font-semibold";
                      
                      return (
                        <div 
                          key={`left-day-${weekIndex}-${dayIndex}`} 
                          className={className}
                          onClick={() => handleDateClick(day.date)}
                        >
                          <div className="relative">{day.day}</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right calendar */}
          <div className="rounded-tl-none rounded-tr-3xs rounded-b-none bg-neutrals-neutral-50 flex flex-col items-start justify-start py-5 px-6 gap-[10px]">
            <div className="w-[280px] flex flex-row items-center justify-between">
              <div 
                className="flex flex-row items-start justify-start p-2.5 cursor-pointer"
                onClick={() => navigateMonth('right', 'prev')}
              >
                <div className="w-5 relative h-5 flex items-center justify-center">←</div>
              </div>
              <div className="relative font-semibold">{monthNames[rightMonth.month]} {rightMonth.year}</div>
              <div 
                className="flex flex-row items-start justify-start p-2.5 cursor-pointer"
                onClick={() => navigateMonth('right', 'next')}
              >
                <div className="w-5 relative h-5 flex items-center justify-center">→</div>
              </div>
            </div>
            <div className="flex flex-col items-start justify-start gap-[4px] text-sm">
              {/* Day headers */}
              <div className="flex flex-row items-start justify-start">
                {dayNames.map((day, index) => (
                  <div key={index} className="w-10 h-10 flex flex-row items-center justify-center p-2.5 box-border">
                    <div className="relative font-semibold">{day}</div>
                  </div>
                ))}
              </div>
              {/* Calendar days */}
              <div className="flex flex-col items-start justify-start gap-[4px]">
                {rightCalendar.map((week, weekIndex) => (
                  <div key={`right-week-${weekIndex}`} className="flex flex-row items-start justify-start">
                    {week.map((day, dayIndex) => {
                      const isStart = day.selectionState === 'start';
                      const isEnd = day.selectionState === 'end';
                      const isRange = day.selectionState === 'range';
                      
                      // Check if this date is shown in the left calendar
                      // and is from an adjacent month (not current month)
                      const isOverlap = !day.isCurrentMonth && 
                                     isDateInCalendarDisplay(day.date, leftMonth.month, leftMonth.year);
                      
                      if (isOverlap) {
                        // Return an empty cell for overlapping dates
                        return (
                          <div 
                            key={`right-day-${weekIndex}-${dayIndex}`} 
                            className="w-10 h-10 flex flex-row items-center justify-center p-2.5 box-border"
                          >
                            <div className="relative text-neutrals-neutral-50">.</div>
                          </div>
                        );
                      }
                      
                      let className = "w-10 h-10 flex flex-row items-center justify-center p-2.5 box-border cursor-pointer";
                      if (!day.isCurrentMonth) className += " text-neutrals-neutral-300";
                      if (isStart) className += " rounded-tl-3xs rounded-bl-3xs bg-primary-primary-500 text-neutrals-neutral-50";
                      if (isEnd) className += " rounded-tr-3xs rounded-br-3xs bg-primary-primary-500 text-neutrals-neutral-50";
                      if (isRange) className += " bg-primary-primary-50 font-semibold";
                      
                      return (
                        <div 
                          key={`right-day-${weekIndex}-${dayIndex}`} 
                          className={className}
                          onClick={() => handleDateClick(day.date)}
                        >
                          <div className="relative">{day.day}</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer with date display and actions */}
        <div className="w-[656px] rounded-t-none rounded-br-3xs rounded-bl-none bg-neutrals-neutral-50 box-border flex flex-row items-center justify-between p-4 border-t-[1px] border-solid border-neutrals-neutral-100">
          <div className="flex flex-row items-center justify-start gap-[10px]">
            <div className="w-[136px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 box-border flex flex-row items-center justify-center py-2.5 px-3.5 border-[1px] border-solid border-neutrals-neutral-100">
              <div className="flex flex-row items-center justify-center">
                <div className="relative">{startDate ? getFormattedDate(startDate) : "Select date"}</div>
              </div>
            </div>
            <div className="relative text-neutrals-neutral-300">-</div>
            <div className="w-[136px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 box-border flex flex-row items-center justify-center py-2.5 px-3.5 border-[1px] border-solid border-neutrals-neutral-100">
              <div className="flex flex-row items-center justify-center">
                <div className="relative">{endDate ? getFormattedDate(endDate) : "Select date"}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-start justify-start gap-[10px] text-sm">
            <div 
              className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 flex flex-row items-center justify-center py-2.5 px-3.5 border-[1px] border-solid border-neutrals-neutral-100 cursor-pointer"
              onClick={onClose}
            >
              <div className="flex flex-row items-center justify-center">
                <div className="relative font-semibold">Cancel</div>
              </div>
            </div>
            <div 
              className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-primary-primary-500 flex flex-row items-center justify-center py-2.5 px-3.5 text-neutrals-neutral-50 cursor-pointer"
              onClick={handleApply}
            >
              <div className="flex flex-row items-center justify-center">
                <div className="relative font-semibold">Apply</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;